import React, { useEffect, useState } from "react"
import Grid from "../../../../grid"
import {
  Container,
  StyledLink,
  Header,
  Title,
  Subtitle,
  StartDate,
  Interviews,
  Separator,
  SearchBox,
  RoleCard
} from "./styles"
import { P } from "../../../../typography"
import Arrow from "../../../../arrow"
import Card from "../../../../card"
import Button from "../../../../button"

const Roles = ({
  label,
  skills,
  employer,
  onRequestInterview,
  onInterview,
  bookInterviewSelectedSkill,
  bookInterviewSection,
  initialSection
}) => {
  const [filteredSkills, setFilteredSkills] = useState(skills)
  const [openSkills, setOpenSkills] = useState([])
  const [searchValue, setSearchValue] = useState("")

  const toggle = (skill) => {
    if (isOpen(skill)) setOpenSkills(openSkills.filter((x) => x.id !== skill.id))
    else setOpenSkills([...openSkills, skill])
  }

  const handleSearchBox = (event) => {
    const { value } = event.target
    setSearchValue(value)

    const filteredSkills = skills.filter((skill) => {
      return (
        skill.name.toLowerCase().includes(value.toLowerCase()) ||
        skill.notes.toLowerCase().includes(value.toLowerCase())
      )
    })
    setFilteredSkills(filteredSkills)
  }

  const isOpen = (skill) => openSkills.includes(skill)
  const hasInterviews = (skill) => {
    return employer?.interviews_available && employer?.interviews_available_role_ids?.includes(skill.id)
  }

  useEffect(() => {
    if (initialSection && initialSection == "roles") {
      setOpenSkills(skills.filter((skill) => hasInterviews(skill)))
    }
  }, [initialSection])

  useEffect(() => {
    if (employer?.available_role_ids_for_programme) {
      const filteredSkills = skills.filter((skill) => {
        return employer.available_role_ids_for_programme.includes(skill.id)
      })
      setFilteredSkills(filteredSkills)
    }
  }, [employer?.available_role_ids_for_programme])

  const panelContent = (skill) => {
    const contents = []

    if (
      employer?.additional_role_fields_enabled &&
      (skill.hourly_wage || skill.experience_required || skill.certifications_required || skill.full_job_description)
    ) {
      contents.push(
        <React.Fragment key="fields">
          <P variant="content" marginless>
            {skill.hourly_wage && (
              <>
                Wage: {employer.employer_currency}
                {skill.hourly_wage} p/h{" "}
                {skill.tips_available !== undefined && skill.tips_available === true && "- Tipped Position"}
                <br />
              </>
            )}
            {skill.experience_required && (
              <>
                Experience required: {skill.experience_required}
                <br />
              </>
            )}
            {skill.certifications_required && (
              <>
                Certifications required: {skill.certifications_required}
                <br />
              </>
            )}
          </P>
          <P variant="content" marginless style={{ marginTop: "-5px", whiteSpace: "pre-wrap" }}>
            {skill.full_job_description}
          </P>
        </React.Fragment>
      )
    }

    if (skill.notes) {
      contents.push(
        <P key="notes" variant="content" marginless style={{ marginTop: "-5px", whiteSpace: "pre-wrap" }}>
          {skill.notes}
        </P>
      )
    }

    if (!employer?.hide_interview_availability) {
      if (bookInterviewSelectedSkill && bookInterviewSelectedSkill.id === skill.id && bookInterviewSection) {
        contents.push(<React.Fragment key="custom-section">{bookInterviewSection()}</React.Fragment>)
      } else if (hasInterviews(skill)) {
        contents.push(
          <Grid key="book" placeItems="start">
            <Button
              variant="primary"
              onClick={() => {
                onInterview(skill)
              }}
            >
              Book Interview
            </Button>
          </Grid>
        )
      } else if (employer?.participantCanInvokeInterviews && !hasInterviews(skill)) {
        contents.push(
          <React.Fragment key="request">
            {!employer.shown_interest && (
              <P variant="content" marginless style={{ marginTop: "-5px" }}>
                Sorry, no interview times are currently available for this role. You can still request an interview.
                We'll email the {employer.host_word.toLowerCase()} to let them know you're interested and you'll get a
                notification once they've added some times.
              </P>
            )}
            {employer.shown_interest && (
              <P variant="content" marginless style={{ marginTop: "-5px" }}>
                You have successfully requested an interview with this {employer.host_word.toLowerCase()}. Once they add
                more interview times, you'll get a notification.
              </P>
            )}
            <Grid placeItems="start">
              <Button
                disabled={employer.shown_interest}
                variant="primary"
                onClick={(e) => {
                  e.preventDefault()
                  onRequestInterview(skill)
                }}
              >
                Request Interview
              </Button>
            </Grid>
          </React.Fragment>
        )
      }
    }

    if (contents.length === 0) return null
    return <>{contents}</>
  }

  return (
    <Container>
      <Grid gap="4px">
        <SearchBox value={searchValue} placeholder="Search" onChange={handleSearchBox} />

        {label && label.length > 0 && (
          <P variant="content" marginless>
            {label}
          </P>
        )}

        {!!employer?.interviews_available_via_event && (
          <RoleCard marginless variant="primary">
            <P marginless>
              This {employer?.host_word?.toLowerCase() || "employer"} is currently open to interviews via an online
              hiring fair.
            </P>
            <P marginless>
              Check <StyledLink to="/events">your events</StyledLink> to register for a hiring fair and access interview
              availability.
            </P>
          </RoleCard>
        )}

        {filteredSkills.length == 0 && (
          <P variant="content" marginless>
            {searchValue?.length > 0 ? (
              <>
                No roles matching <strong>{searchValue}</strong> were found.
              </>
            ) : (
              <>No roles are currently available.</>
            )}
          </P>
        )}

        {filteredSkills.map((skill) => {
          const content = panelContent(skill)
          const hasContent = !!content

          return (
            <Grid gap="16px" key={skill.id}>
              <div></div>

              <Header {...(hasContent ? { onClick: () => toggle(skill), style: { cursor: "pointer" } } : {})}>
                <Grid gap="4px">
                  <Title variant="content" marginless>
                    {skill.name}
                  </Title>
                  <Subtitle showSeparator={!employer?.hide_interview_availability}>
                    <StartDate variant="content" marginless>
                      Start date: {skill.start_dates_string}
                    </StartDate>

                    {!employer?.hide_interview_availability && (
                      <Interviews positive={hasInterviews(skill)}>
                        {hasInterviews(skill) ? "Interviews available" : "Interviews unavailable"}
                      </Interviews>
                    )}
                  </Subtitle>
                </Grid>

                {hasContent && <Arrow direction={isOpen(skill) ? "up" : "down"} color="current" size="small" />}
              </Header>

              {hasContent && isOpen(skill) && content}

              {filteredSkills.indexOf(skill) < filteredSkills.length - 1 && <Separator variant="secondaryReadable" />}
            </Grid>
          )
        })}
      </Grid>
    </Container>
  )
}

Roles.defaultProps = {
  skills: []
}

export default Roles
